import {bootstrapModule} from 'src/utilities/vuex'
import {axiosError} from 'src/utilities/utils'
import {objectById, formatIsoDate, priorityLabel, sortBy} from 'src/utilities/utils.js'
import Constants from 'src/utilities/constants'
import _ from 'lodash'
import moment from 'moment'
import {createAndDownloadWorksheet} from 'src/utilities/excel'
import {getCompanies, getFiles, getJobs, getPlanNames, getProjects} from 'src/utilities/ptracApiHelper'
import {ptracGetEntities} from 'src/utilities/ptracApi'
import {OK} from 'http-status-codes'

let state = {
  selected: [],
  selectedStatus: [],
  results: [],
  dueDate: null,
  nameId: null
}

const _myTasksStore = {
  namespaced: true,
  state,
  getters: {
    results: (state) => {
      let tasks = state.results
      if (state.dueDate) {
        tasks = _.filter(state.results, (task) => formatIsoDate(task.dueDate) === state.dueDate)
      }
      if (state.nameId) {
        tasks = _.filter(tasks, (task) => task.nameId === state.nameId)
      }
      return tasks
    },
    uniqueTaskNames: (state) => {
      const kvps = []
      _.uniqBy(state.results, 'name').forEach((task) => {
        kvps.push(
          {
            label: task.name,
            value: task.nameId
          }
        )
      })
      return sortBy(kvps, 'label')
    }
  },
  mutations: {
    delete: (state, taskId) => {
      state.results = _.filter(state.results, (task) => task.id !== taskId)
    },
    reset: (state) => {
      state.results = []
    },
    filterByDueDate: (state, dueDate) => {
      state.dueDate = formatIsoDate(dueDate)
    },
    sort: (state, sortedTaskData) => {
      sortedTaskData.forEach((sortedTaskDatum) => {
        const sortedAllTask = objectById(state.results, sortedTaskDatum.id)
        if (sortedAllTask) {
          sortedAllTask.sort = sortedTaskDatum.sort
        }
      })
    }
  },
  actions: {
    async fetchByAssigneeAndStatus({state, rootState}, payload) {
      state.results = [] // start fresh

      if (payload.userId) {
        const url = 'tasks/search/findByAssigneeIdAndStatusInOrderByDueDateAsc?status=' + payload.taskStatus.join(',') + '&assigneeId=' + payload.userId
        try {
          const response = await ptracGetEntities(url, 'ptracTasks')

          let tasks = []
          if (OK === response.status) {
            tasks = response.data
          }

          if (tasks.length > 0) {
            const projectMap = await getProjects(tasks, 'projectId')
            const jobMap = await getJobs(tasks, 'jobId')
            const jobs = Array.from(jobMap.values())
            const companyMap = await getCompanies(jobs, 'clientId', 'builderId')
            const planNameMap = await getPlanNames(jobs, 'planNameId')
            const planNames = Array.from(planNameMap.values())
            const fileMap = await getFiles(planNames, 'fileId')

            for (const task of tasks) {
              task.orphaned = false
              task.taskTemplate = objectById(rootState.tasks.templates.results, task.templateId)
              task.priorityLabel = priorityLabel(task.priority)
              task.priority = +task.priority // Coerced to number to allow column sorting

              if (projectMap.has(task.projectId)) {
                task.project = projectMap.get(task.projectId)
              }
              if (jobMap.get(task.jobId)) {
                task.job = jobMap.get(task.jobId)
              }

              if (!task.project) {
                task.orphaned = true
                task.project = {name: 'ORPHANED'}
                continue
              }

              if (!task.job) {
                task.orphaned = true
                task.job = {name: 'ORPHANED'}
                continue
              }

              task.job.priorityLabel = priorityLabel(task.job.priority)
              task.job.priority = +task.job.priority // Coerced to number to allow column sorting

              if (companyMap) {
                if (task.job.clientId && companyMap.has(task.job.clientId)) {
                  task['job']['client'] = companyMap.get(task.job.clientId)
                }
                if (task.job.builderId && companyMap.has(task.job.builderId)) {
                  task['job']['builder'] = companyMap.get(task.job.builderId)
                }
              }

              if (task.job.planNameId && planNames.length > 0) {
                let planName = planNames.find(planName => planName.id === task.job.planNameId)
                if (planName) {
                  task.job.planName = planName
                }

                if (_.get(task, 'job.planName.fileId')) {
                  if (fileMap.length > 0 && fileMap.has(task.job.planName.fileId)) {
                    task.job.planName.file = fileMap.get(task.job.planName.fileId)
                  }
                }
              }

              const owner = _.get(rootState, ['users', 'resultsMap', task.ownerId], null)
              if (task.ownerId && owner) {
                task.owner = owner
              }
            }

            state.results = tasks
          }
        } catch (err) {
          return {
            result: Constants.RETURN_CODES.ERROR,
            errors: axiosError([], err, url)
          }
        }
      }
    },
    resultsToExcel({state, rootState}) {
      const dateVal = moment()

      const user = rootState.security.user
      const assignee = _.get(user, 'profile.firstName') + ' ' + _.get(user, 'profile.lastName')

      const fileName = 'my-ptrac-tasks-' + dateVal.format('MM-DD-YYYY') + '.xlsx'
      const tabName = 'My Tasks'
      const header = [
        {
          header: 'Name',
          key: 'name',
          width: 30
        }, {
          header: 'Assignee',
          key: 'assignee',
          width: 20
        }, {
          header: 'Due Date',
          key: 'dueDate',
          width: 25
        }, {
          header: 'Status',
          key: 'status',
          width: 15
        }, {
          header: 'Project Name',
          key: 'projectName',
          width: 25
        }, {
          header: 'Street Number',
          key: 'streetNumber',
          width: 15
        }, {
          header: 'Street Name',
          key: 'streetName',
          width: 20
        }, {
          header: 'City',
          key: 'city',
          width: 20
        }, {
          header: 'Job Name',
          key: 'jobName',
          width: 30
        }, {
          header: 'Job Coordinator',
          key: 'jobCoordinator',
          width: 20
        }, {
          header: 'Builder',
          key: 'builder',
          width: 25
        }, {
          header: 'Client',
          key: 'client',
          width: 25
        }]

      const rows = []
      state.results.forEach((task) => {
        const coordinator = objectById(rootState.users.results, task.job.coordinatorId)

        rows.push(
          {
            name: task.name,
            assignee: assignee,
            dueDate: new Date(task.dueDate),
            status: task.status,
            projectName: task.project.name,
            streetNumber: _.get(task, 'project.address.streetNumber'),
            streetName: _.get(task, 'project.address.streetName'),
            city: _.get(task, 'project.address.city'),
            jobName: task.job.name,
            jobCoordinator: _.get(coordinator, 'name'),
            builder: _.get(task, 'job.builder.name'),
            client: _.get(task, 'job.client.name')
          }
        )
      })

      createAndDownloadWorksheet(fileName, tabName, header, rows, user)
    }
  }
}

export {_myTasksStore}

export default bootstrapModule(_myTasksStore)
